export type ScanAssetResult<TPayload> =
    { kind: 'ready'; payload: TPayload } | { kind: 'generating' } | { kind: 'empty' };

export type ScanImagesView<TImage> =
    | { status: 'loading' }
    | { status: 'generating' }
    | { status: 'empty' }
    | { status: 'error'; message: string }
    | { status: 'ready'; images: readonly TImage[] };

export interface ScanImagesInput<TImage> {
    result: ScanAssetResult<readonly TImage[]> | undefined;
    isFetching: boolean;
    errorMessage: string | null;
}

// Images already on screen stay through a background refetch; anything else shows the skeleton.
export function scanImagesView<TImage>({
    result,
    isFetching,
    errorMessage
}: ScanImagesInput<TImage>): ScanImagesView<TImage> {
    if (result?.kind === 'ready' && result.payload.length > 0) {
        return { status: 'ready', images: result.payload };
    }
    if (isFetching) return { status: 'loading' };
    if (errorMessage !== null) return { status: 'error', message: errorMessage };
    if (!result) return { status: 'loading' };
    if (result.kind === 'generating') return { status: 'generating' };
    return { status: 'empty' };
}

export type ScanDownloadPhase =
    { phase: 'downloading'; percent: number | null } | { phase: 'unzipping' };

export const INITIAL_DOWNLOAD_PHASE: ScanDownloadPhase = { phase: 'downloading', percent: null };

export interface ScanMeshCatalog {
    files: readonly unknown[];
    hasPly: boolean;
}

export type ScanViewerView<TMeshes> =
    | { status: 'downloading'; percent: number | null }
    | { status: 'unzipping' }
    | { status: 'generating' }
    | { status: 'empty' }
    | { status: 'no-meshes' }
    | { status: 'ply-only' }
    | { status: 'error'; message: string }
    | { status: 'ready'; meshes: TMeshes };

export interface ScanViewerInput<TMeshes> {
    result: ScanAssetResult<TMeshes> | undefined;
    isFetching: boolean;
    errorMessage: string | null;
    phase: ScanDownloadPhase;
}

export function scanViewerView<TMeshes extends ScanMeshCatalog>({
    result,
    isFetching,
    errorMessage,
    phase
}: ScanViewerInput<TMeshes>): ScanViewerView<TMeshes> {
    if (isFetching || (!result && errorMessage === null)) {
        return phase.phase === 'unzipping'
            ? { status: 'unzipping' }
            : { status: 'downloading', percent: phase.percent };
    }
    if (errorMessage !== null) return { status: 'error', message: errorMessage };
    if (!result || result.kind === 'empty') return { status: 'empty' };
    if (result.kind === 'generating') return { status: 'generating' };
    if (result.payload.files.length > 0) return { status: 'ready', meshes: result.payload };
    return result.payload.hasPly ? { status: 'ply-only' } : { status: 'no-meshes' };
}

const PERCENT = 100;

// A zero or missing total means the server sent no Content-Length: show a spinner, not a guess.
export function downloadPercent(loadedBytes: number, totalBytes: number | null): number | null {
    if (!totalBytes || totalBytes <= 0) return null;
    return Math.min(PERCENT, Math.max(0, Math.floor((loadedBytes / totalBytes) * PERCENT)));
}
